import { useMemo, useCallback, useRef } from 'react';

import { getEditorInlineCommentOptions } from '@confluence/annotation-provider-inline-comments/entry-points/editor.ts';
import {
	useAnnotationsDispatch,
	useAnnotationManager,
} from '@confluence/annotation-provider-store/entry-points/AnnotationsContext.ts';
import { fg } from '@confluence/feature-gating';
import { useAddCommentPermissionCheck } from '@confluence/comments-hooks/entry-points/useAddCommentPermissionCheck.ts';
import { useContentType } from '@confluence/page-context';
import { useActiveInlineData } from '@confluence/experiment-inline-reactions/entry-points/useActiveInlineData.ts';
import { useInlineReactionMarkers } from '@confluence/experiment-inline-reactions/entry-points/useInlineReactionMarkers.ts';

export const useEditorAnnotationProviderOptions = ({
	pageId,
	editorActions,
	triggerAnalyticsEvent,
	updateUnresolvedInlineComment,
}: {
	pageId: string;
	editorActions: any;
	triggerAnalyticsEvent: any;
	updateUnresolvedInlineComment: any;
}) => {
	const { setEditorCreateProps, setEditorViewProps } = useAnnotationsDispatch();
	const annotationManager = useAnnotationManager();
	const [contentType] = useContentType();

	const { canAddComments } = useAddCommentPermissionCheck(pageId);

	const canAddCommentsRef = useRef(canAddComments);
	canAddCommentsRef.current = canAddComments;

	const getCanAddComments = useCallback(() => canAddCommentsRef.current, []);

	const { reactionMarkerRefs, reactionMarkerMap } = useActiveInlineData(pageId);
	const { mergedReactionMarkerMap, optimisticResolvedMarkerRefs } = useInlineReactionMarkers({
		reactionMarkerRefs,
		reactionMarkerMap,
	});

	const mergedReactionMarkerMapRef = useRef(mergedReactionMarkerMap);
	mergedReactionMarkerMapRef.current = mergedReactionMarkerMap;
	const getReactionMarkerMap = useCallback(() => mergedReactionMarkerMapRef.current, []);
	const optimisticResolvedMarkerRefsRef = useRef(optimisticResolvedMarkerRefs);
	optimisticResolvedMarkerRefsRef.current = optimisticResolvedMarkerRefs;
	const getOptimisticResolvedMarkerRefs = useCallback(
		() => optimisticResolvedMarkerRefsRef.current,
		[],
	);

	return useMemo(
		() => ({
			inlineComment: getEditorInlineCommentOptions({
				pageId,
				editorActions,
				triggerAnalyticsEvent,
				updateUnresolvedInlineComment,
				setEditorCreateProps,
				setEditorViewProps,
				editorAnnotationProviderManagersEnabled: fg(
					'confluence_frontend_editor_annotation_manager',
				),
				getCanAddComments,
				contentType,
				getReactionMarkerMap,
				getOptimisticResolvedMarkerRefs,
			}),
			annotationManager,
		}),
		[
			pageId,
			triggerAnalyticsEvent,
			editorActions,
			updateUnresolvedInlineComment,
			setEditorCreateProps,
			setEditorViewProps,
			annotationManager,
			contentType,
			getCanAddComments,
			getReactionMarkerMap,
			getOptimisticResolvedMarkerRefs,
		],
	);
};
